<app-configuration-page
  title="Gift Certificates"
  description="Issue, look up, and manage gift certificates. Redeem a certificate by entering its code to attach the student to the pre-paid package."
  [breadcrumbs]="[{ label: 'Gift Certificates' }]">

  <ng-container configPageActions>
    <button *ngIf="_permissionsService.canAccess('gift_certificates_redeem')"
            mat-stroked-button color="primary" type="button"
            (click)="openRedeemDialog()">
      <mat-icon>redeem</mat-icon>
      Redeem
    </button>
    <button *ngIf="_permissionsService.canAccess('gift_certificates_create')"
            mat-flat-button color="primary" type="button"
            (click)="openGiftCertificateDialog(null, 'create')">
      <mat-icon>add</mat-icon>
      Add Gift Certificate
    </button>
  </ng-container>

  <nav mat-tab-nav-bar configPageTabs #tabPanel>
    <a mat-tab-link
       *ngFor="let link of links"
       [routerLink]="link.path"
       [queryParams]="link.queryParams"
       routerLinkActive #rla="routerLinkActive"
       [active]="rla.isActive"
       (click)="activeLink = link">
      {{link.label}}
    </a>
  </nav>

  <!-- One surface: the toolbar sits on the card's top edge and the table runs
       edge-to-edge beneath it, rather than a card wrapping an inset table. -->
  <mat-card class="configuration-card gift-certificates-card">
    <mat-card-content>
      <div class="table-toolbar d-flex align-items-center flex-wrap gap-2 px-3 py-2">
        <span class="table-toolbar-count text-muted small flex-grow-1">
          {{ dataSource?.filteredData?.length || 0 }} of {{ totalCount }} certificates
        </span>

        <!-- Search sits at the toolbar's right edge and expands on demand,
             the way a spreadsheet grid presents it. -->
        <div class="table-toolbar-actions d-flex align-items-center ms-auto">
          <button mat-icon-button
                  type="button"
                  aria-label="Refresh"
                  matTooltip="Refresh"
                  [disabled]="ShowProgressBar"
                  (click)="refreshView()">
            <mat-icon>refresh</mat-icon>
          </button>

          <button *ngIf="!searchOpen"
                  mat-icon-button
                  type="button"
                  aria-label="Search"
                  matTooltip="Search"
                  (click)="openSearch()">
            <mat-icon>search</mat-icon>
          </button>

          <mat-form-field *ngIf="searchOpen"
                          appearance="outline"
                          class="table-toolbar-search mb-0">
            <input matInput
                   #quickSearchInput
                   [value]="searchValue"
                   (input)="applyQuickFilter($event.target.value)"
                   placeholder="Search certificates">
            <mat-icon matPrefix>search</mat-icon>
            <button matSuffix
                    mat-icon-button
                    type="button"
                    aria-label="Close search"
                    (click)="closeSearch()">
              <mat-icon>close</mat-icon>
            </button>
          </mat-form-field>
        </div>
      </div>

      <mat-progress-bar [class.invisible]="!ShowProgressBar" mode="indeterminate"></mat-progress-bar>

      <div class="text-center py-5 text-muted"
           *ngIf="!ShowProgressBar && (dataSource?.filteredData?.length || 0) === 0">
        <mat-icon style="font-size: 40px; width: 40px; height: 40px;">card_giftcard</mat-icon>
        <h3 class="mt-2 mb-1">No gift certificates in this view</h3>
        <p class="mb-0" *ngIf="searchValue">Try clearing the search or switching status tabs.</p>
        <p class="mb-0" *ngIf="!searchValue">Certificates you issue will appear here.</p>
      </div>

      <div class="table-responsive"
           [class.d-none]="!ShowProgressBar && (dataSource?.filteredData?.length || 0) === 0">

        <table multiTemplateDataRows mat-table #displayTable [dataSource]="dataSource" matSort
                 appGridTable gridTableKey="gift-certificates">
          <mat-menu #giftCertificateMenu="matMenu">
            <ng-template matMenuContent let-element="element">
              <button mat-menu-item
                *ngIf="_permissionsService.canAccess('gift_certificates_redeem') && (element.field_gift_certificate_status === 'active' || element.field_gift_certificate_status === 'pending')"
                (click)="openRedeemDialog(element.field_gift_certificate_code)">
                <mat-icon>redeem</mat-icon>
                Redeem
              </button>
              <button mat-menu-item
                *ngIf="_permissionsService.canAccess('gift_certificates_edit') && (element.field_gift_certificate_status === 'active' || element.field_gift_certificate_status === 'pending')"
                (click)="cancelGiftCertificate(element)">
                <mat-icon>block</mat-icon>
                Cancel Gift Certificate
              </button>
              <button mat-menu-item (click)="printGiftCertificate(element)">
                <mat-icon>print</mat-icon>
                Print Certificate
              </button>
              <button mat-menu-item
                *ngIf="_permissionsService.canAccess('gift_certificates_edit') && !element.email_sent_date && (element.field_gift_certificate_status === 'active' || element.field_gift_certificate_status === 'pending')"
                (click)="openScheduleDialog(element)">
                <mat-icon>schedule_send</mat-icon>
                {{ element.field_email_send_date ? 'Change Scheduled Email' : 'Schedule Email' }}
              </button>
              <button mat-menu-item *ngIf="_permissionsService.canAccess('gift_certificates_edit')" (click)="resendGiftCertificateEmail(element)">
                <mat-icon>email</mat-icon>
                {{ element.email_sent_date ? 'Resend Email' : 'Send Email Now' }}
              </button>
              <button mat-menu-item
                *ngIf="_permissionsService.canAccess('gift_certificates_edit') && element.field_recipient_phone"
                (click)="sendGiftCertificateSms(element)">
                <mat-icon>sms</mat-icon>
                {{ element.sms_sent_date ? 'Resend Text Message' : 'Send Text Message' }}
              </button>
              <button mat-menu-item
                *ngIf="_permissionsService.canAccess('gift_certificates_delete')"
                (click)="deleteGiftCertificate(element)">
                <mat-icon>delete</mat-icon>
                Delete Gift Certificate
              </button>
            </ng-template>
          </mat-menu>

          <ng-container matColumnDef="expand">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let element">
              <button mat-icon-button (click)="toggleRow(element)" [attr.aria-expanded]="!!expandedElements[element.id]" aria-label="expand row">
                <mat-icon>{{ expandedElements[element.id] ? 'expand_less' : 'expand_more' }}</mat-icon>
              </button>
            </td>
          </ng-container>

          <ng-container matColumnDef="field_gift_certificate_code">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Code</th>
            <td mat-cell *matCellDef="let element">{{element.field_gift_certificate_code}}</td>
          </ng-container>

          <ng-container matColumnDef="field_recipient_name">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Recipient</th>
            <td mat-cell *matCellDef="let element">{{element.field_recipient_name}}</td>
          </ng-container>

          <ng-container matColumnDef="field_gift_certificate_amount">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="text-end">Amount</th>
            <td mat-cell *matCellDef="let element" class="text-end">{{element.field_gift_certificate_amount | studioCurrency }}</td>
          </ng-container>

          <ng-container matColumnDef="field_remaining_balance">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="text-end">Remaining</th>
            <td mat-cell *matCellDef="let element" class="text-end">{{element.field_remaining_balance | studioCurrency }}</td>
          </ng-container>

          <ng-container matColumnDef="field_gift_certificate_status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
            <td mat-cell *matCellDef="let element">
              <span class="grid-pill"
                    [ngClass]="'grid-pill-' + (element.field_gift_certificate_status || '')">
                {{element.field_gift_certificate_status}}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="field_purchase_date">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Purchase Date</th>
            <td mat-cell *matCellDef="let element">{{ reportDateService.formatDate(element.field_purchase_date) }}</td>
          </ng-container>

          <ng-container matColumnDef="email">
            <th mat-header-cell *matHeaderCellDef>Delivery</th>
            <td mat-cell *matCellDef="let element">
              <div class="delivery-line"><span class="delivery-label">Email</span>{{ emailStatus(element) }}</div>
              <div class="delivery-line" *ngIf="element.field_recipient_phone">
                <span class="delivery-label">Text</span>{{ smsStatus(element) }}
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="field_expiration_date">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Expiration Date</th>
            <td mat-cell *matCellDef="let element">{{ reportDateService.formatDate(element.field_expiration_date) }}</td>
          </ng-container>

          <ng-container matColumnDef="edit">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let element">
              <button
                mat-icon-button
                [matMenuTriggerFor]="giftCertificateMenu"
                [matMenuTriggerData]="{ element: element }"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
            </td>
          </ng-container>

          <ng-container matColumnDef="expandedDetail">
            <td mat-cell *matCellDef="let element" [attr.colspan]="displayedColumns.length">
              <div class="element-detail" [@detailExpand]="expandedElements[element.id] ? 'expanded' : 'collapsed'">
                <div class="detail-panel">
                  <!-- Who: the two people on the certificate, then the sale itself. -->
                  <div class="detail-groups">
                    <section class="detail-group">
                      <h6 class="detail-heading">Recipient</h6>
                      <dl class="detail-list">
                        <div class="detail-item"><dt>Name</dt><dd>{{ element.field_recipient_name || '—' }}</dd></div>
                        <div class="detail-item"><dt>Email</dt><dd>{{ element.field_recipient_email || '—' }}</dd></div>
                        <div class="detail-item"><dt>Phone</dt><dd>{{ element.field_recipient_phone || '—' }}</dd></div>
                      </dl>
                    </section>
                    <section class="detail-group">
                      <h6 class="detail-heading">Purchaser</h6>
                      <dl class="detail-list">
                        <div class="detail-item"><dt>Name</dt><dd>{{ element.field_purchaser_name || '—' }}</dd></div>
                        <div class="detail-item"><dt>Email</dt><dd>{{ element.field_purchaser_email || '—' }}</dd></div>
                        <div class="detail-item"><dt>Phone</dt><dd>{{ element.field_purchaser_phone || '—' }}</dd></div>
                      </dl>
                    </section>
                    <section class="detail-group">
                      <h6 class="detail-heading">Sale</h6>
                      <dl class="detail-list">
                        <div class="detail-item"><dt>Purchased at</dt><dd>{{ element.studio_purchaser_title || '—' }}</dd></div>
                        <div class="detail-item"><dt>Redeemed at</dt><dd>{{ element.field_gift_certificate_location || 'Not redeemed' }}</dd></div>
                        <div class="detail-item"><dt>Checkout</dt><dd class="text-capitalize">{{ element.field_checkout_type || '—' }}</dd></div>
                        <div class="detail-item"><dt>Payment</dt><dd>{{ element.field_gift_certificate_payment || '—' }}</dd></div>
                      </dl>
                    </section>
                  </div>

                  <!-- Both texts edit in place: click the text (or the pencil) to
                       open a textarea, Enter or Save to keep it, Escape to drop it. -->
                  <div class="detail-notes">
                    <ng-container *ngTemplateOutlet="inlineText; context: {
                      element: element,
                      field: 'field_gift_message',
                      label: 'Gift message',
                      hint: 'Shown to the recipient on the certificate and in its email.',
                      empty: 'Add a gift message',
                      quote: true
                    }"></ng-container>
                    <ng-container *ngTemplateOutlet="inlineText; context: {
                      element: element,
                      field: 'field_gift_certificate_notes',
                      label: 'Notes',
                      hint: 'Staff only. The recipient never sees these.',
                      empty: 'Add a note',
                      quote: false
                    }"></ng-container>
                  </div>
                </div>
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr mat-row *matRowDef="let element; columns: displayedColumns;"
            class="element-row"
            [class.expanded]="expandedElements[element.id]">
          </tr>
          <tr mat-row *matRowDef="let row; columns: ['expandedDetail']; when: isExpansionDetailRow"
            class="detail-row"
            [class.d-none]="!expandedElements[row.id]">
          </tr>
        </table>

      </div>

      <ng-template #inlineText let-element="element" let-field="field" let-label="label" let-hint="hint" let-empty="empty" let-quote="quote">
        <section class="inline-text"
                 [class.inline-text-editing]="isEditing(element, field)"
                 [class.inline-text-quote]="quote">
          <header class="inline-text-header">
            <h6 class="detail-heading">{{ label }}</h6>
            <span class="inline-text-hint" *ngIf="isEditing(element, field)">{{ hint }}</span>
            <button *ngIf="canEditText() && !isEditing(element, field)"
                    mat-icon-button
                    type="button"
                    class="inline-text-edit-btn"
                    [attr.aria-label]="'Edit ' + label"
                    [matTooltip]="'Edit ' + label"
                    (click)="startEdit(element, field)">
              <mat-icon>edit</mat-icon>
            </button>
          </header>

          <ng-container *ngIf="!isEditing(element, field)">
            <!-- A value reads as text; an empty slot reads as an invitation. -->
            <div *ngIf="element[field]"
                 class="inline-text-value"
                 [class.inline-text-clickable]="canEditText()"
                 [attr.role]="canEditText() ? 'button' : null"
                 [attr.tabindex]="canEditText() ? 0 : null"
                 [attr.title]="canEditText() ? 'Click to edit' : null"
                 (click)="startEdit(element, field)"
                 (keydown.enter)="startEdit(element, field)">{{ element[field] }}</div>
            <button *ngIf="!element[field] && canEditText()"
                    type="button"
                    class="inline-text-add"
                    (click)="startEdit(element, field)">
              <mat-icon>add</mat-icon>
              {{ empty }}
            </button>
            <div *ngIf="!element[field] && !canEditText()" class="inline-text-none">None</div>
          </ng-container>

          <ng-container *ngIf="isEditing(element, field)">
            <textarea class="inline-text-input"
                      [id]="'gc-edit-' + element.id + '-' + field"
                      rows="3"
                      [placeholder]="empty"
                      [disabled]="isSaving(element, field)"
                      [(ngModel)]="editing[element.id + ':' + field].value"
                      [ngModelOptions]="{ standalone: true }"
                      (keydown)="onEditKeydown($event, element, field)"></textarea>
            <footer class="inline-text-actions">
              <button mat-flat-button color="primary" type="button"
                      class="inline-text-save"
                      [disabled]="isSaving(element, field)"
                      (click)="saveEdit(element, field)">{{ isSaving(element, field) ? 'Saving…' : 'Save' }}</button>
              <button mat-button type="button"
                      class="inline-text-cancel"
                      [disabled]="isSaving(element, field)"
                      (click)="cancelEdit(element, field)">Cancel</button>
              <span class="inline-text-keys"><kbd>Enter</kbd> saves · <kbd>Shift</kbd>+<kbd>Enter</kbd> new line · <kbd>Esc</kbd> cancels</span>
            </footer>
          </ng-container>
        </section>
      </ng-template>

      <mat-paginator
        [class.d-none]="!ShowProgressBar && (dataSource?.filteredData?.length || 0) === 0"
        [pageSizeOptions]="[10, 25, 50, 100]"
        [pageSize]="25"
        aria-label="Select page of gift certificates"></mat-paginator>

    </mat-card-content>
  </mat-card>

</app-configuration-page>
